<script lang="ts" setup>
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { computed, onMounted, type PropType, ref, watch } from 'vue';
import type { MenuOption } from '@/models/menu/menu-option';
import { NIcon } from 'naive-ui';
import { ArrowLeft16Regular } from '@vicons/fluent';
import { useI18n } from 'vue-i18n';
import { useTransportRoutingStore } from '@/stores/transport/transport-routing-store';
import { useRateGroupStore} from '@/stores/rate-group/rate-group-store';

const { t } = useI18n();

const props = defineProps({
  menuOptions: {
    type: Array as PropType<MenuOption[]>,
    required: true,
  },
  isDetailPage: {
    type: Boolean,
    required: false,
  },
});

const route = useRoute();
const router = useRouter();
const transportRoutingStore = useTransportRoutingStore();
const rateGroupStore = useRateGroupStore();

const currentMenuOption = ref<MenuOption | null>(null);

onMounted(() => {
  updateCurrentMenuOption();
});

watch(
  () => route.matched,
  () => {
    updateCurrentMenuOption();
  }
);

const updateCurrentMenuOption = () => {
  const currentRouteConfig = router.options.routes.find((r) => r.path === route.matched[0]?.path) || null;
  currentMenuOption.value = props.menuOptions.find((o) => o.key == currentRouteConfig?.name)!;
};

const transportTitle = computed<string>(() => {
  if(route.params.id){
    const cities = transportRoutingStore.routing.actions.map((action) => action.address.city).filter((city, index, arr) => index === 0 || city !== arr[index - 1]);
    return cities.length ? cities.join('/') : t('page_header.transport.title');
  }

  return t('page_header.transport.title');
});

const rateGroupDetailTitle = computed<string>(() => {
  return t('page_header.management.sub_pages.rate_group_details', { name: rateGroupStore.rateGroup.name });
});

const rateGroupSegmentsTitle = computed<string>(() => {
  return t('page_header.management.sub_pages.rate_group_segments', { name: rateGroupStore.rateGroup.name });
});

const pageTitle = computed<string>(() => {
  if (route.path.startsWith('/transport/')) {
    return transportTitle.value;
  }

  if (route.path.startsWith('/management/rate-group/')) {
    return route.path.endsWith('/segments') 
        ? rateGroupSegmentsTitle.value 
        : rateGroupDetailTitle.value;
  }

  return currentMenuOption.value?.label ?? '';
});

watch(() => props.menuOptions, () => {
  updateCurrentMenuOption();
}, { immediate: true });
</script>
<template>
  <div class="page-header" :class="isDetailPage ? 'page-header--detail' : null">
    <nav v-if="isDetailPage" class="header-back">
      <a @click="router.push(route.matched[0]?.path ?? '/')">
        <n-icon size="16">
          <arrow-left16-regular />
        </n-icon>
        {{ t('page_header.back_to_overview') }}
      </a>
    </nav>

    <div class="page-title">
      {{ pageTitle }}
    </div>

    <nav v-if="!isDetailPage" class="header-nav">
      <ul>
        <template v-for="subMenu in currentMenuOption?.subMenus ?? []">
          <li v-if="subMenu.show" :key="subMenu.key">
            <router-link :to="subMenu.to" :data-text="subMenu.label">
              {{ subMenu.label }}
            </router-link>
          </li>
        </template>
      </ul>
    </nav>
  </div>
</template>

<style lang="scss" scoped>
.page-header {
  height: var(--page-header-height);
  padding: var(--dis-space-1_0x) var(--dis-space-4_0x) 0;
  background: linear-gradient(90deg, var(--dis-color-other-gradient_from_left) 0%, var(--dis-color-other-gradient_to_right) 100%);
  border-top: 1px solid color-mix(in srgb, var(--dis-color-neutral-white) 16%, transparent);
  display: flex;
  flex-direction: column;

  &--detail {
    .page-title {
      line-height: 1;
    }
  }
}

.page-title {
  font-size: var(--dis-font-size-h1);
  line-height: var(--dis-font-lineheight-h1);
  font-weight: var(--dis-font-weight-strong);
  color: var(--dis-color-text-pagetitle);
}

.header-back {
  a {
    display: inline-flex;
    align-items: center;
    gap: var(--dis-space-1_0x);
    height: 30px;
    cursor: pointer;
    color: var(--dis-color-text-pagetitle);
    opacity: 0.7;
  }
}

.header-nav {
  margin: 0 calc(var(--dis-space-1_0x) * -1);
  margin-top: auto;

  ul {
    a {
      height: 30px;
      justify-content: start;
      cursor: pointer;
    }
  }
}
</style>
